import { useEffect, useState } from "react";
import api from "@/lib/api";
import { usePagedData } from "@/hooks/usePagedData";
import { useSite } from "@/context/SiteContext";
import { num, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { PackageCheck } from "lucide-react";

const TYPES = ["101 GR", "561 Initial Stock", "501 Receipt w/o PO", "201 GI Cost Center",
  "261 GI Production Order", "551 Scrapping", "311 Transfer Posting"];

export function MigoPanel() {
  const { sites, activeSite } = useSite();
  const { items: materials } = usePagedData("/materials", 200);
  const site0 = activeSite !== "GROUP" ? activeSite : (sites[0]?.id || "UK");
  const [f, setF] = useState({ movement_type: "101 GR", material_code: "", site_id: site0, qty: "", ref: "", cost_center: "", dest_site: "" });
  const [moves, setMoves] = useState([]);
  const isIssue = ["201 GI Cost Center", "261 GI Production Order", "551 Scrapping"].includes(f.movement_type);
  const isTransfer = f.movement_type === "311 Transfer Posting";

  const loadMoves = () => api.get("/stock-movements").then((r) => setMoves(r.data.slice(0, 25))).catch(() => setMoves([]));
  useEffect(() => { loadMoves(); }, []);

  const post = async () => {
    if (!f.material_code || !f.qty) { toast.error("Pick a material and enter a quantity"); return; }
    const m = materials.find((x) => x.material_code === f.material_code);
    try {
      await api.post("/goods-movements", { ...f, material_name: m?.name || f.material_code, qty: Number(f.qty) });
      toast.success(`Material document posted · ${f.movement_type}`);
      setF({ ...f, material_code: "", qty: "", ref: "", cost_center: "" });
      loadMoves();
    } catch (e) { toast.error(e?.response?.data?.detail || "Movement failed"); }
  };

  const inp = "rounded-md border border-[#c8cfda] bg-white px-2 py-1.5 text-sm outline-none focus:border-[#0D9488]";
  return (
    <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
      <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm" data-testid="migo-panel">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Goods Movement · EP-MM10 · <span className="normal-case text-[#9aa0a6]">S/4 MIGO</span></h3>
        <div className="mt-4 space-y-3">
          <div><div className="text-[11px] uppercase text-[#6a6d70]">Movement Type</div>
            <select data-testid="migo-type" value={f.movement_type} onChange={(e) => setF({ ...f, movement_type: e.target.value })} className={`mt-1 w-full ${inp}`}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div><div className="text-[11px] uppercase text-[#6a6d70]">Material</div>
            <select data-testid="migo-material" value={f.material_code} onChange={(e) => setF({ ...f, material_code: e.target.value })} className={`mt-1 w-full ${inp}`}>
              <option value="">Select material…</option>{(materials || []).map((m) => <option key={m.material_code} value={m.material_code}>{m.name}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-[11px] uppercase text-[#6a6d70]">Plant / Site</div>
              <select data-testid="migo-site" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} className={`mt-1 w-full ${inp}`}>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div><div className="text-[11px] uppercase text-[#6a6d70]">Quantity</div>
              <input data-testid="migo-qty" type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} className={`mt-1 w-full ${inp}`} /></div>
          </div>
          {isTransfer && (
            <div><div className="text-[11px] uppercase text-[#6a6d70]">Receiving Plant</div>
              <select data-testid="migo-dest" value={f.dest_site} onChange={(e) => setF({ ...f, dest_site: e.target.value })} className={`mt-1 w-full ${inp}`}>
                <option value="">Select…</option>{sites.filter((s) => s.id !== f.site_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>)}
          {isIssue && (
            <div><div className="text-[11px] uppercase text-[#6a6d70]">Cost Centre / Order</div>
              <input data-testid="migo-cc" value={f.cost_center} onChange={(e) => setF({ ...f, cost_center: e.target.value })} className={`mt-1 w-full ${inp}`} placeholder="CC-PACK" /></div>)}
          <div><div className="text-[11px] uppercase text-[#6a6d70]">Reference / Document</div>
            <input data-testid="migo-ref" value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} className={`mt-1 w-full ${inp}`} placeholder="PO / delivery / note" /></div>
          <button data-testid="migo-post-btn" onClick={post} className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#0D9488] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0b7d73]">
            <PackageCheck size={16} /> Post Material Document</button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#e5e9f0] bg-white shadow-sm" data-testid="migo-moves-table">
        <div className="border-b border-[#eff1f4] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Recent Material Documents</div>
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-[#e5e9f0] bg-[#f7f8fa] text-[11px] uppercase tracking-wide text-[#6a6d70]">
            <th className="px-3 py-2.5">Type</th><th className="px-3 py-2.5">Material</th><th className="px-3 py-2.5">Site</th><th className="px-3 py-2.5">Qty</th><th className="px-3 py-2.5">Ref</th><th className="px-3 py-2.5">Date</th></tr></thead>
          <tbody>
            {moves.map((m) => (
              <tr key={m.id} className="border-b border-[#eff1f4] last:border-0">
                <td className="px-3 py-2 font-mono text-xs text-[#0D9488]">{m.movement_type}</td>
                <td className="px-3 py-2">{m.material_name || m.material_code}</td>
                <td className="px-3 py-2 font-mono text-xs">{m.site_id}</td>
                <td className="px-3 py-2 font-mono">{num(m.qty)}</td>
                <td className="px-3 py-2 font-mono text-xs text-[#6a6d70]">{m.ref}</td>
                <td className="px-3 py-2 text-xs text-[#6a6d70]">{shortDate(m.timestamp)}</td>
              </tr>))}
            {moves.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#6a6d70]">No material documents yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
